import React, { useState, useEffect } from "react";
import {
  Grid,
  Container,
  Typography,
  Box,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TablePagination,
  TextField,
  Chip,
  Alert,
  Button,
} from "@mui/material";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import { useNavigate, useParams } from "react-router-dom";
import { RemoveRedEyeOutlined, Edit } from "@mui/icons-material";
import { useGetApi } from "../hooks/useGetApi";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import MedViewDetails from "../components/nursing/modals/med-view-details";
import UpdateMedicineStatusModal from "../components/nursing/modals/update-medicine-status-modal";
import {
  DEFAULT_LIMIT,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
} from "../utils/constants";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import { getMedicineRequestsApi } from "../services/medicine-tracking.service";

const headLabel = [
  {
    id: 1,
    label: "SNO",
    value: "sNo",
  },
  {
    id: 2,
    label: "Item Code",
    value: "itemCode",
  },
  {
    id: 3,
    label: "Medicine Name",
    value: "medicineName",
  },
  {
    id: 4,
    label: "Requested Quantity",
    value: "requestedQuantity",
  },
  {
    id: 5,
    label: "Issued Quantity",
    value: "issuedQuantity",
  },
  {
    id: 6,
    label: "Status",
    value: "status",
  },
  {
    id: 7,
    label: "Actions",
    value: "",
    align: "right",
  },
];

const getStatusColor = (status) => {
  switch (status) {
    case "Pending":
      return "#E00700";
    case "Approved":
      return "#61AD1A";
    case "Rejected":
      return "#FF0000";
    default:
      return "#666666";
  }
};

const getStatusChipColor = (status) => {
  switch (status) {
    case "Pending":
      return "error";
    case "Approved":
      return "success";
    case "Rejected":
      return "error";
    default:
      return "default";
  }
};

const NursingRequestTracking = () => {
    const { patientId, visitId } = useParams();
    const navigate = useNavigate();
    const [allData, setAllData] = useState(null);
    const [medReqTrackList, setMedReqTrackList] = useState([]);
    const [patientInfo, setPatientInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [medReqTrackCount, setMedReqTrackCount] = useState(0);
    
    // Add missing state variables
    const [selectedRow, setSelectedRow] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedMedicine, setSelectedMedicine] = useState(null);
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    
    const { dataList: apiData, dataCount, isLoading: hookLoading, isError: hookError, refetch } = useGetApi({
        apiFunction: getMedicineRequestsApi,
        params: { patientId, visitId },
        dependencies: [patientId, visitId],
        rawData: true,
    });

  useEffect(() => {
      if (apiData) {
        setAllData(apiData);
        const list = Array.isArray(apiData?.medicineRequests) ? apiData.medicineRequests : [];
        setMedReqTrackList(list);
        setMedReqTrackCount(list.length);
        setPatientInfo(apiData?.patientInfo || null);
      } else {
        setAllData(null);
        setMedReqTrackList([]);
        setPatientInfo(null);
        setMedReqTrackCount(0);
      }

      setIsLoading(hookLoading);
      setIsError(!!hookError);
  }, [apiData, hookLoading, hookError]);

  const handleOpenModal = (row) => {
    setSelectedRow(row);
    setModalOpen(true);
  };

  const handleOpenStatusModal = (row) => {
    setSelectedMedicine(row);
    setStatusModalOpen(true);
  };

  const handleStatusUpdateSuccess = () => {
    refetch();
  };

  const filteredList = medReqTrackList?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Auto-refresh data every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, 30000);

    return () => clearInterval(interval);
  }, [refetch]);

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              padding: 2,
              marginTop: 2,
              borderRadius: 2,
              marginRight: "90px",
            }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                color: "#5D6A82",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: "bold",
                padding: 0,
                transition: "none !important",
                marginBottom: "16px",
              }}
            >
              Back
            </Button>
            {/* table  */}
            {isLoading ? (
              <Loader />
            ) : isError ? (
              <ErrorMessage />
            ) : (
              <>
              {/* Filter */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 0,
                  mb: "20px",
                  color: "#5D6A82",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Open Sans",
                    fontWeight: 600,
                    fontSize: "20px",
                    lineHeight: "29px",
                    letterSpacing: "0px",
                    color: "inherit",
                  }}
                >
                  Medicine Request Tracking
                </Typography>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Chip 
                    label={`Total Requests: ${medReqTrackList.length}`}
                    color="primary"
                    variant="outlined"
                  />
                  <Chip 
                    label={`Pending: ${medReqTrackList.filter(item => item.status === "Pending").length}`}
                    color="error"
                    variant="outlined"
                  />
                  <Chip 
                    label={`Approved: ${medReqTrackList.filter(item => item.status === "Approved").length}`}
                    color="success"
                    variant="outlined"
                  />
                  <Chip 
                    label={`Rejected: ${medReqTrackList.filter(item => item.status === "Rejected").length}`}
                    color="error"
                    variant="outlined"
                  />
                </Box>
              </Box>

              {/* Patient Information */}
              {patientInfo?.uhid && (
                <Box
                  sx={{
                    border: `1px solid #CCD4DA`,
                    borderRadius: "12px",
                    p: "20px",
                    mb: "30px",
                  }}
                >
                  <Typography sx={{ color: "#5D6A82", mb: 2, fontSize: "14px" }}>
                    Patient Information
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <TextField
                        label="UHID"
                        value={patientInfo?.uhid || ""}
                        fullWidth
                        required
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <TextField
                        label="Patient Name"
                        value={patientInfo?.patientName || ""}
                        fullWidth
                        required
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <TextField
                        label="Ward Name"
                        fullWidth
                        value={patientInfo?.wardName || ""}
                        required
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <TextField
                        label="Bed Number"
                        fullWidth
                        value={patientInfo?.bedNumber || ""}
                        required
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* No Data Alert */}
              {medReqTrackList.length === 0 && !isLoading && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No medicine requests found for this patient. Please submit a medicine request first.
                </Alert>
              )}

              {/* Medicine Requests Table */}
              {medReqTrackList.length > 0 && (
                <TableContainer component={Paper}>
                  <Table
                    aria-label="nursing all medication tracking"
                    sx={{ minWidth: 800 }}
                  >
                    <TableHead
                      sx={{
                        backgroundColor: "#f5f5f5",
                        "& th": { fontWeight: "bold" },
                      }}
                    >
                      <TableRow>
                        {headLabel?.map((option) => (
                          <TableCell
                            key={option?.id}
                            sx={{ textAlign: option?.align || "left" }}
                          >
                            {option?.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredList?.map((row) => (
                        <TableRow key={`${row?.trackingId}-${row?.medicineId}`}>
                          {headLabel?.map((column, index) =>
                            index !== headLabel?.length - 1 ? (
                              <TableCell
                                key={column?.id}
                                sx={{
                                  color: column?.value === "status" 
                                    ? getStatusColor(row?.[column?.value])
                                    : "inherit",
                                }}
                              >
                                {column?.value === "status" ? (
                                  <Chip
                                    label={row?.[column?.value] || "Pending"}
                                    color={getStatusChipColor(row?.[column?.value])}
                                    size="small"
                                    variant="outlined"
                                  />
                                ) : (
                                  row?.[column?.value] || "-"
                                )}
                              </TableCell>
                            ) : (
                              <TableCell
                                key={column?.id}
                                sx={{ textAlign: "right" }}
                              >
                                <Box sx={{ display: "flex", gap: 1 }}>
                                  <IconButton 
                                    onClick={() => handleOpenStatusModal(row)}
                                    size="small"
                                    title="Update Status"
                                  >
                                    <Edit sx={{ fontSize: "16px" }} />
                                  </IconButton>
                                  <IconButton 
                                    onClick={() => handleOpenModal(row)}
                                    size="small"
                                    title="View Details"
                                  >
                                    <RemoveRedEyeOutlined sx={{ fontSize: "16px" }} />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            )
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Pagination */}
              {medReqTrackList.length > 0 && (
                <TablePagination
                  rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                  component="div"
                  count={medReqTrackCount}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              )}
            </>
            )}
          </Box>
        </Box>
        <MedViewDetails
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          details={selectedRow}
        />
        <UpdateMedicineStatusModal
          open={statusModalOpen}
          onClose={() => setStatusModalOpen(false)}
          trackingId={selectedMedicine?.trackingId}
          medicineId={selectedMedicine?.medicineId}
          currentStatus={selectedMedicine?.status}
          onSuccess={handleStatusUpdateSuccess}
        />
      </Box>
    </Container>
  );
};

export default NursingRequestTracking;
